import { getStripeScriptUrl } from '@/constants/change-plan.js';

const MESSAGES = {
  error: 'We can\'t seem to access our servers. Refresh to try again.',
};

class StripeElements {
  constructor(store, apiKey) {
    this.apiKey = apiKey;
    this.store = store;
    this.stripe = null;

    // Persistent form vars
    this.element = null;
    this.elements = null;
    this.returnUrl = '';
  }

  // Helper functions
  setLoadingState() {
    this.store.commit('stripeElements/setError', '');
    this.store.commit('stripeElements/setIsLoading', true);
    this.store.commit('changePlan/setIsProcessing', true);
  }

  resetForm() {
    this.returnUrl = '';
    this.elements = null;
    this.element = null;
  }

  async init() {
    if (process.client && this.apiKey) {
      const betaKeys = {
        betas: ['process_order_beta_1'],
        apiVersion: '2022-08-01; orders_beta=v4',
      };

      if (this.stripe) {
        return Promise.resolve();
      }

      if (window.Stripe) {
        this.stripe = window.Stripe(this.apiKey, betaKeys);
        return Promise.resolve();
      }

      return new Promise((resolve) => {
        const script = document.createElement('script');
        script.setAttribute('type', 'text/javascript');
        script.setAttribute('src', getStripeScriptUrl());
        document.getElementsByTagName('head')[0].appendChild(script);

        script.onload = () => {
          this.stripe = window.Stripe(this.apiKey, betaKeys);
          resolve();
        };
      });
    }

    return Promise.resolve();
  }

  initForm(clientSecret, returnUrl) {
    if (!(this.stripe && clientSecret && returnUrl)) {
      this.store.commit('stripeElements/setError', MESSAGES.error);
      return;
    }

    const options = {
      clientSecret,
      appearance: { theme: 'stripe' },
    };

    this.store.commit('stripeElements/setIsReady', false);
    this.store.commit('stripeElements/setIsLoading', true);

    this.resetForm();
    this.returnUrl = returnUrl;
    this.elements = this.stripe.elements(options);
    this.element = this.elements.create('payment', {
      defaultValues: {
        billingDetails: this.store.getters['stripeElements/billingDetails'],
      },
    });

    this.element.on('ready', () => {
      this.store.commit('stripeElements/setIsReady', true);
      this.store.commit('stripeElements/setIsLoading', false);
    });
  }

  retrieveOrder(clientSecret) {
    this.setLoadingState();

    this.stripe.retrieveOrder(clientSecret).then(({ order }) => {
      this.store.commit('stripeElements/setOrder', order);
      this.store.commit('stripeElements/setIsLoading', false);
    }).catch(() => {
      this.store.commit('stripeElements/setError', MESSAGES.error);
      this.store.commit('stripeElements/setIsLoading', false);
    });
  }

  async confirmPayment() {
    try {
      this.setLoadingState();

      const { error } = await this.stripe.processOrder({
        elements: this.elements,
        confirmParams: {
          return_url: this.returnUrl,
        },
      });

      if (error) {
        this.store.commit('stripeElements/setError', error.message);
        return error?.message || 'There was an error with your payment';
      }

      // If no error reset the stripe elements
      this.resetForm();
    } catch (error) {
      this.store.commit('stripeElements/setError', error.message);
      return error || 'Unable to complete payment, unknown error';
    } finally {
      this.store.commit('stripeElements/setIsLoading', false);
      this.store.commit('changePlan/setIsProcessing', false);
    }

    return null;
  }
}

export default (context, inject) => {
  const { store, $config } = context;
  const apiKey = $config.stripePublicKey;
  const stripeElements = new StripeElements(store, apiKey);

  inject('stripeElements', stripeElements);
  context.$stripeElements = stripeElements;
};
